/**
 * Workspace settings (GET/PATCH /api/workspace/settings). PATCH deep-merges `settings`; a null
 * value removes a key. Successful updates refresh the cached session as well.
 */
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "./client";
import { sessionQueryKey, type Session, type ShipTo, type Workspace, type WorkspaceSettings } from "./session";

/** A JSON merge patch: every key optional at every depth, and null deletes the key. */
export type MergePatch<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: MergePatch<T[K]> | null }
    : T;

export interface WorkspacePatch {
  name?: string;
  ship_to?: Partial<ShipTo>;
  market_country?: string;
  currency?: string;
  compliance_market?: string;
  /** Deep-merged into the stored settings; null deletes a key. */
  settings?: MergePatch<WorkspaceSettings>;
}

export const workspaceQueryKey = ["workspace", "settings"] as const;

export const workspaceQuery = queryOptions({
  queryKey: workspaceQueryKey,
  queryFn: ({ signal }) => apiGet<Workspace>("/workspace/settings", { signal }),
});

export function patchWorkspace(patch: WorkspacePatch): Promise<Workspace> {
  return apiPatch<Workspace>("/workspace/settings", patch);
}

/** PATCH the workspace and update both the settings query and the session. */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchWorkspace,
    onSuccess: (workspace) => {
      queryClient.setQueryData(workspaceQueryKey, workspace);
      queryClient.setQueryData<Session>(sessionQueryKey, (session) =>
        session ? { ...session, workspace, settings: workspace.settings } : session,
      );
    },
  });
}
